/**
 * Comprehensive Graph API Live Test Suite
 * Tests all email and calendar operations as a user would prompt the AI
 * Uses actual Microsoft Graph and Google APIs (NOT Blink SDK)
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Play, 
  Mail, 
  Calendar, 
  Search, 
  Send, 
  Edit, 
  Trash2,
  Reply,
  Download,
  AlertCircle
} from 'lucide-react';
import { createClient } from '@blinkdotnew/sdk';
import { toast } from 'sonner';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

interface TestResult {
  id: string;
  name: string;
  category: 'email' | 'calendar';
  userPrompt: string;
  authMethod: 'blink_auth' | 'oauth_outlook' | 'oauth_gmail';
  graphApiEndpoint: string;
  httpMethod: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
  statusCode?: number;
  duration?: number;
  errorMessage?: string;
  successMessage?: string;
  responseData?: any;
  apiCallDetails?: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body?: any;
    response?: any;
  };
}

export default function ComprehensiveGraphAPITest() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      setUser(state.user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (user?.id) {
      checkConnectedProviders();
    }
  }, [user]);

  const checkConnectedProviders = async () => {
    try {
      const connections = await blink.db.oauthConnections.list({
        where: { userId: user.id, connected: '1' },
        limit: 10
      });

      const providers = connections.map((conn: any) => conn.provider);
      setConnectedProviders(providers);
    } catch (error) {
      console.error('Failed to check providers:', error);
    }
  };

  const initializeTests = (): TestResult[] => {
    const hasOutlook = connectedProviders.includes('outlook');
    const hasGmail = connectedProviders.includes('gmail');

    return [
      // Email Operations
      {
        id: 'email-send-1',
        name: 'Send Simple Email',
        category: 'email',
        userPrompt: 'Send an email to test@example.com with subject "Test Email" and body "This is a test"',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/sendMail',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-send-2',
        name: 'Send Email with CC/BCC',
        category: 'email',
        userPrompt: 'Send an email to user@example.com, CC manager@example.com, BCC archive@example.com with subject "Team Update"',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/sendMail',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-send-3',
        name: 'Send High Priority Email',
        category: 'email',
        userPrompt: 'Send a high priority email to urgent@example.com with subject "URGENT: Action Required"',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/sendMail',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-search-1',
        name: 'Search Recent Emails',
        category: 'email',
        userPrompt: 'Search for emails with the word "meeting" in the subject',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages',
        httpMethod: 'GET',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-search-2',
        name: 'Search by Sender',
        category: 'email',
        userPrompt: 'Find all emails from john@example.com',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages',
        httpMethod: 'GET',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-search-3',
        name: 'Search with Attachments',
        category: 'email',
        userPrompt: 'Show me all emails with attachments from last week',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages',
        httpMethod: 'GET',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-draft-1',
        name: 'Create Draft Email',
        category: 'email',
        userPrompt: 'Create a draft email to team@example.com with subject "Weekly Update"',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-reply-1',
        name: 'Reply to Email',
        category: 'email',
        userPrompt: 'Reply to the last email with "Thanks for the update!"',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages/{id}/reply',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-move-1',
        name: 'Move Email to Folder',
        category: 'email',
        userPrompt: 'Move the email to Archive folder',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages/{id}/move',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'email-delete-1',
        name: 'Delete Email',
        category: 'email',
        userPrompt: 'Delete the spam email',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/messages/{id}',
        httpMethod: 'DELETE',
        status: hasOutlook ? 'pending' : 'skipped'
      },

      // Calendar Operations
      {
        id: 'calendar-list-1',
        name: 'List Events This Week',
        category: 'calendar',
        userPrompt: 'Show me all calendar events for this week',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events',
        httpMethod: 'GET',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'calendar-create-1',
        name: 'Create Meeting Event',
        category: 'calendar',
        userPrompt: 'Schedule a meeting titled "Team Standup" tomorrow at 10am for 30 minutes',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'calendar-create-2',
        name: 'Create All-Day Event',
        category: 'calendar',
        userPrompt: 'Create an all-day event for "Company Holiday" next Friday',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'calendar-create-3',
        name: 'Create Online Meeting',
        category: 'calendar',
        userPrompt: 'Schedule an online meeting with attendees alice@example.com and bob@example.com',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events',
        httpMethod: 'POST',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'calendar-search-1',
        name: 'Search Events by Title',
        category: 'calendar',
        userPrompt: 'Find all events with "review" in the title',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events',
        httpMethod: 'GET',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'calendar-update-1',
        name: 'Update Event Time',
        category: 'calendar',
        userPrompt: 'Reschedule the team standup to 11am',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events/{id}',
        httpMethod: 'PATCH',
        status: hasOutlook ? 'pending' : 'skipped'
      },
      {
        id: 'calendar-delete-1',
        name: 'Delete Calendar Event',
        category: 'calendar',
        userPrompt: 'Cancel and delete the meeting',
        authMethod: hasOutlook ? 'oauth_outlook' : 'blink_auth',
        graphApiEndpoint: '/me/calendar/events/{id}',
        httpMethod: 'DELETE',
        status: hasOutlook ? 'pending' : 'skipped'
      }
    ];
  };

  const runAllTests = async () => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    if (connectedProviders.length === 0) {
      toast.error('Please connect Outlook or Gmail first');
      return;
    }

    setIsRunning(true);
    const tests = initializeTests();
    setResults(tests);
    setProgress(0);

    let completed = 0;

    for (const test of tests) {
      if (test.status === 'skipped') {
        completed++;
        setProgress((completed / tests.length) * 100);
        continue;
      }

      await runTest(test);
      completed++;
      setProgress((completed / tests.length) * 100);
      
      // Wait between tests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setIsRunning(false);
    toast.success('All tests completed!');
  };

  const runTest = async (test: TestResult) => {
    const startTime = Date.now();

    setResults(prev => prev.map(t => 
      t.id === test.id ? { ...t, status: 'running' as const } : t
    ));

    try {
      let result: any;

      switch (test.category) {
        case 'email':
          result = await runEmailTest(test);
          break;
        case 'calendar':
          result = await runCalendarTest(test);
          break;
      }

      const duration = Date.now() - startTime;

      setResults(prev => prev.map(t => 
        t.id === test.id ? {
          ...t,
          status: 'passed' as const,
          duration,
          successMessage: result.message,
          responseData: result.data,
          apiCallDetails: result.apiCallDetails,
          statusCode: result.statusCode
        } : t
      ));

    } catch (error: any) {
      const duration = Date.now() - startTime;

      setResults(prev => prev.map(t => 
        t.id === test.id ? {
          ...t,
          status: 'failed' as const,
          duration,
          errorMessage: error.message || 'Test failed',
          apiCallDetails: error.apiCallDetails,
          statusCode: error.statusCode
        } : t
      ));
    }
  };

  const runEmailTest = async (test: TestResult): Promise<any> => {
    console.log(`🧪 Test ${test.id}: Starting LIVE Graph API test`);
    console.log(`   User Prompt: "${test.userPrompt}"`);
    console.log(`   Expected Endpoint: ${test.graphApiEndpoint}`);
    console.log(`   Expected Method: ${test.httpMethod}`);

    // Get real OAuth access token from database
    const accounts = await blink.db.emailAccounts.list({
      where: { userId: user.id },
      limit: 1
    });

    if (!accounts || accounts.length === 0) {
      throw new Error('No email account connected. Connect Outlook or Gmail first.');
    }

    const account = accounts[0];
    console.log(`✅ Found email account: ${account.provider} (${account.emailAddress || account.email_address})`);

    // Get valid access token (refreshes if needed)
    const { getValidAccessToken } = await import('@/services/oauthService');
    const accessToken = await getValidAccessToken(account.provider, user.id);

    if (!accessToken) {
      throw new Error(`No valid access token for ${account.provider}`);
    }

    console.log(`🔐 Got valid access token for ${account.provider}`);

    // Extract parameters from user prompt using AI
    const { gpt5NanoAgent } = await import('@/services/gpt5NanoAgent');
    const response = await gpt5NanoAgent.processUserIntent(test.userPrompt);
    
    if (!response.validation.isValid) {
      throw new Error(`Validation failed: ${response.validation.errors.join(', ')}`);
    }

    console.log(`🤖 Agent parsed action: ${response.action.type}`, response.action.parameters);

    // NOW MAKE THE REAL GRAPH API CALL DIRECTLY
    // This bypasses any simulation and ensures we're testing the actual API
    const result = await makeRealGraphAPICall(
      account.provider,
      accessToken,
      test.graphApiEndpoint,
      test.httpMethod,
      response.action.parameters
    );

    console.log(`✅ REAL Graph API call completed successfully`);
    console.log(`   Status Code: ${result.statusCode}`);
    console.log(`   Response:`, result.data ? JSON.stringify(result.data).substring(0, 200) : 'No data');

    return {
      message: `Successfully executed ${response.action.type} via REAL Microsoft Graph API`,
      data: result.data,
      statusCode: result.statusCode,
      apiCallDetails: {
        url: result.url,
        method: test.httpMethod,
        headers: { 'Authorization': 'Bearer [REDACTED]', 'Content-Type': 'application/json' },
        actualEndpoint: result.url,
        expectedEndpoint: `https://graph.microsoft.com/v1.0${test.graphApiEndpoint}`,
        response: result.data,
        isRealAPICall: true,
        provider: account.provider
      }
    };
  };

  // Helper function to make real Graph API calls
  const makeRealGraphAPICall = async (
    provider: string,
    accessToken: string,
    endpoint: string,
    method: string,
    parameters: any
  ): Promise<{ statusCode: number; data: any; url: string }> => {
    const baseUrl = provider === 'gmail' 
      ? 'https://gmail.googleapis.com/gmail/v1'
      : 'https://graph.microsoft.com/v1.0';

    let url = `${baseUrl}${endpoint}`;
    const options: RequestInit = {
      method,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    };

    // Handle different operation types
    if (method === 'POST' || method === 'PATCH') {
      const body: any = {};

      // Email operations
      if (endpoint.includes('sendMail')) {
        body.message = {
          subject: parameters.subject || 'Test Email',
          body: {
            contentType: 'Text',
            content: parameters.body || 'This is a test email from live API test'
          },
          toRecipients: (parameters.to || ['test@example.com']).map((email: string) => ({
            emailAddress: { address: email }
          }))
        };
        if (parameters.cc) {
          body.message.ccRecipients = parameters.cc.map((email: string) => ({
            emailAddress: { address: email }
          }));
        }
        if (parameters.importance) {
          body.message.importance = parameters.importance;
        }
      }
      // Draft operations
      else if (endpoint.includes('/messages') && !endpoint.includes('sendMail')) {
        body.subject = parameters.subject || 'Test Draft';
        body.body = {
          contentType: 'Text',
          content: parameters.body || 'This is a test draft'
        };
        body.toRecipients = (parameters.to || ['test@example.com']).map((email: string) => ({
          emailAddress: { address: email }
        }));
      }
      // Calendar operations
      else if (endpoint.includes('/events')) {
        body.subject = parameters.title || parameters.subject || 'Test Event';
        body.start = {
          dateTime: parameters.start || new Date(Date.now() + 86400000).toISOString(),
          timeZone: 'UTC'
        };
        body.end = {
          dateTime: parameters.end || new Date(Date.now() + 90000000).toISOString(),
          timeZone: 'UTC'
        };
        if (parameters.attendees) {
          body.attendees = parameters.attendees.map((email: string) => ({
            emailAddress: { address: email },
            type: 'required'
          }));
        }
      }
      // Move/reply operations
      else if (endpoint.includes('/move') || endpoint.includes('/reply')) {
        if (parameters.destinationId) {
          body.destinationId = parameters.destinationId;
        }
        if (parameters.comment) {
          body.comment = parameters.comment;
        }
      }

      options.body = JSON.stringify(body);
    }
    // Handle GET with query parameters
    else if (method === 'GET') {
      const queryParams = new URLSearchParams();
      
      if (parameters.query) {
        queryParams.append('$search', `"${parameters.query}"`);
      }
      if (parameters.$top) {
        queryParams.append('$top', parameters.$top.toString());
      }
      if (parameters.$select) {
        queryParams.append('$select', parameters.$select);
      }
      
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    console.log(`📡 Making REAL Graph API request:`);
    console.log(`   URL: ${url}`);
    console.log(`   Method: ${method}`);
    console.log(`   Body:`, options.body ? JSON.stringify(safeJsonParse(options.body), null, 2).substring(0, 300) : 'None');

    const response = await fetch(url, options);
    
    let data = null;
    if (response.status !== 204) {
      const text = await response.text();
      try {
        data = text ? safeJsonParse(text) : null;
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      const errorMsg = data?.error?.message || data || `HTTP ${response.status}`;
      throw new Error(`Graph API call failed: ${errorMsg}`);
    }

    return {
      statusCode: response.status,
      data,
      url
    };
  };

  const runCalendarTest = async (test: TestResult): Promise<any> => {
    console.log(`🧪 Test ${test.id}: Starting LIVE Graph API calendar test`);
    console.log(`   User Prompt: "${test.userPrompt}"`);

    // Use the same real API call approach as email tests
    return await runEmailTest(test);
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'passed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'running':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'skipped':
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getCategoryIcon = (category: TestResult['category']) => {
    switch (category) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'calendar':
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getStats = () => {
    const passed = results.filter(r => r.status === 'passed').length;
    const failed = results.filter(r => r.status === 'failed').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    const total = results.length;

    return { passed, failed, skipped, total };
  };

  const exportResults = () => {
    const data = JSON.stringify(results, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `graph-api-test-results-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Results exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Clock className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to run tests</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Comprehensive Graph API Test Suite</h1>
          <p className="text-muted-foreground mt-1">
            Live tests of Microsoft Graph and Google APIs as a user would prompt the AI
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runAllTests} disabled={isRunning} size="lg">
            <Play className="mr-2 h-4 w-4" />
            {isRunning ? 'Running Tests...' : 'Run All Tests'}
          </Button>
          {results.length > 0 && (
            <Button onClick={exportResults} variant="outline" size="lg">
              <Download className="mr-2 h-4 w-4" />
              Export Results
            </Button>
          )}
        </div>
      </div>

      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle>Connected Providers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {connectedProviders.length === 0 ? (
              <Badge variant="destructive">No providers connected</Badge>
            ) : (
              connectedProviders.map(provider => (
                <Badge key={provider} variant="default" className="capitalize">
                  {provider}
                </Badge>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Dashboard */}
      {results.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Total Tests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-green-600">Passed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.passed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-red-600">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.failed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-600">Skipped</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-600">{stats.skipped}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Progress Bar */}
      {isRunning && (
        <Card>
          <CardHeader>
            <CardTitle>Test Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-2">
              {Math.round(progress)}% complete
            </p>
          </CardContent>
        </Card>
      )}

      {/* Test Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Results</CardTitle>
            <CardDescription>
              {stats.passed} passed · {stats.failed} failed · {stats.skipped} skipped
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-4">
                {results.map((result) => (
                  <Card key={result.id} className="border">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(result.status)}
                          <div>
                            <div className="flex items-center gap-2">
                              {getCategoryIcon(result.category)}
                              <h3 className="font-semibold">{result.name}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {result.userPrompt}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant="outline" className="capitalize">
                            {result.authMethod.replace('oauth_', '')}
                          </Badge>
                          {result.duration && (
                            <span className="text-xs text-muted-foreground">
                              {result.duration}ms
                            </span>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Badge variant="secondary">
                            {result.httpMethod}
                          </Badge>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {result.graphApiEndpoint}
                          </code>
                          {result.statusCode && (
                            <Badge variant={result.statusCode === 200 ? 'default' : 'destructive'}>
                              {result.statusCode}
                            </Badge>
                          )}
                        </div>

                        {result.successMessage && (
                          <div className="text-sm text-green-600 bg-green-50 dark:bg-green-950 p-2 rounded">
                            ✓ {result.successMessage}
                          </div>
                        )}

                        {result.errorMessage && (
                          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 p-2 rounded">
                            ✗ {result.errorMessage}
                          </div>
                        )}

                        {result.apiCallDetails && (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                              API Call Details
                            </summary>
                            <pre className="mt-2 p-2 bg-muted rounded overflow-x-auto">
                              {JSON.stringify(result.apiCallDetails, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {results.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Ready to Test</CardTitle>
            <CardDescription>
              Click "Run All Tests" to start testing all Graph API operations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              This test suite will execute {initializeTests().length} comprehensive tests covering:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-muted-foreground">
              <li>Email sending (simple, with CC/BCC, with priority)</li>
              <li>Email searching (by content, sender, attachments)</li>
              <li>Email operations (draft, reply, move, delete)</li>
              <li>Calendar management (list, create, update, delete events)</li>
              <li>Online meetings and attendee management</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
